// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { styled } from '@mui/material/styles';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { Box, Icon, Typography } from '../../../lib/base';
import { Link } from '../../../lib/link';
import * as routes from '../../../routes';

const RootContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingTop: theme.spacing(1),
  },
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  [theme.breakpoints.down('md')]: {
    flexWrap: 'wrap',
  },
  [theme.breakpoints.up('md')]: {
    flexWrap: 'nowrap',
  },
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.primary.main,
}));

const LeftHeaderContainer = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flex: '0 1 auto',
  minWidth: '0',
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  [theme.breakpoints.up('sm')]: {
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
}));

const RightHeaderContainer = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1),
  },
  [theme.breakpoints.up('sm')]: {
    marginBottom: theme.spacing(2),
  },
}));

const SlashTypography = styled(Typography)(({ theme }) => ({
  color: theme.custom.colors.common.white,
  marginRight: theme.spacing(1),
  marginLeft: theme.spacing(1),
  overflow: 'initial',
}));

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.custom.colors.common.white,
  textDecoration: 'underline',
  '&:hover': {
    color: theme.custom.colors.common.darkWhite,
  },
  overflow: 'initial',
  marginRight: theme.spacing(1),
}));

const SelectedBreadcrumbTypography = styled(Typography)(({ theme }) => ({
  color: theme.custom.colors.common.darkWhite,
  // textDecoration: 'underline',
  overflow: 'initial',
}));

interface Props {
  readonly id?: string;
  readonly title: string;
  readonly name: string;
  readonly pluralName: string;
  readonly pluralRoute: string;
  readonly groupName?: string;
  readonly groupRoute?: string;
  readonly searchRoute: string;
  readonly icon?: string;
  readonly noBreadcrumb?: boolean;
  readonly backgroundColorClassName?: string;
  readonly rightElement?: React.ReactElement;
}

function PageViewHeader({
  id,
  title,
  name,
  pluralName,
  pluralRoute,
  groupName,
  groupRoute,
  icon,
  noBreadcrumb,
  rightElement,
}: Props): React.ReactElement {
  const breadcrumbVariant = 'body1';
  let iconElement = null;
  if (icon != null) {
    iconElement = (
      <Icon sx={{ marginRight: (theme) => theme.spacing(1), color: (theme) => theme.custom.colors.common.white }}>
        {icon}
      </Icon>
    );
  }
  let idElement = null;
  if (id != null) {
    idElement = (
      <Typography
        sx={{
          flex: '0 1 auto',
          minWidth: '0',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: (theme) => theme.custom.colors.common.white,
        }}
        variant="body2"
      >
        {id}
      </Typography>
    );
  }
  const slash = <SlashTypography variant={breadcrumbVariant}>/</SlashTypography>;
  const breadCrumb = noBreadcrumb ? null : (
    <Box display="flex" flexDirection="row">
      <StyledLink variant={breadcrumbVariant} path={routes.DASHBOARD} title="Home" />
      {slash}
      <StyledLink variant={breadcrumbVariant} path={pluralRoute} title={pluralName} />
      {groupRoute && groupName && (
        <>
          {slash}
          <StyledLink variant={breadcrumbVariant} path={groupRoute} title={groupName} />
        </>
      )}
      <SlashTypography sx={{ display: { xs: 'none', sm: 'block' } }} variant={breadcrumbVariant}>
        /
      </SlashTypography>
      <SelectedBreadcrumbTypography sx={{ display: { xs: 'none', sm: 'block' } }} variant={breadcrumbVariant}>
        {name}
      </SelectedBreadcrumbTypography>
    </Box>
  );

  return (
    <RootContainer>
      <LeftHeaderContainer>
        <Box display="flex" flexDirection="column">
          <Box>
            {iconElement}
            <Typography
              sx={{ marginRight: (theme) => theme.spacing(1), color: (theme) => theme.custom.colors.common.white }}
              component="h2"
              variant="h4"
            >
              {title}
            </Typography>
            {idElement}
          </Box>
          {breadCrumb}
        </Box>
      </LeftHeaderContainer>
      <RightHeaderContainer>{rightElement}</RightHeaderContainer>
    </RootContainer>
  );
}

export default PageViewHeader;
